import React, { useState, useContext } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { DayPicker } from "react-day-picker";

import { cn } from "@/lib/utils";
import { buttonVariants } from "@/components/ui/button";
import { LanguageProviderContext } from "@/providers/LanguageProvider";

export type CalendarProps = React.ComponentProps<typeof DayPicker>;

function Calendar({
  className,
  classNames,
  showOutsideDays = true,
  onMonthChange,
  ...props
}: CalendarProps) {
  const [month, setMonth] = useState<Date>(props.defaultMonth || new Date());
  const { translator } = useContext(LanguageProviderContext);

  const handleMonthChange = (newMonth: Date) => {
    setMonth(newMonth);
    if (onMonthChange) {
      onMonthChange(newMonth);
    }
  };

  return (
    <DayPicker
      showOutsideDays={!showOutsideDays}
      className={cn("p-3", className)}
      month={month}
      onMonthChange={handleMonthChange}
      classNames={{
        caption_label: "text-sm font-medium",
        month_caption: "flex justify-center items-center h-10 relative",
        month_grid: "w-full border-collapse space-y-1",
        weekdays: "flex",
        weekday: "text-muted-foreground rounded-md w-9 font-normal text-[0.8rem]",
        week: "flex w-full mt-2",
        day: "h-9 w-9 text-center text-sm p-0 relative [&:has([aria-selected].day-range-end)]:rounded-r-md [&:has([aria-selected].day-outside)]:bg-accent/50 [&:has([aria-selected])]:bg-accent first:[&:has([aria-selected])]:rounded-l-md last:[&:has([aria-selected])]:rounded-r-md focus-within:relative focus-within:z-20",
        day_button: "h-9 w-9 p-0 font-normal aria-selected:opacity-100 rounded-md transition-colors hover:bg-accent hover:text-accent-foreground",
        range_end: "day-range-end",
        today: "border border-primary/50 rounded-md font-semibold",
        selected: "bg-primary text-primary-foreground hover:bg-primary/90 hover:text-primary-foreground focus:bg-primary focus:text-primary-foreground rounded-md font-semibold",
        outside: "day-outside text-muted-foreground opacity-50 aria-selected:bg-accent/50 aria-selected:text-muted-foreground aria-selected:opacity-30",
        disabled: "text-muted-foreground opacity-50",
        range_middle: "aria-selected:bg-accent aria-selected:text-accent-foreground",
        hidden: "invisible",
        ...classNames,
      }}
      components={{
        Nav: ({ onPreviousClick, onNextClick }) => {
          const goToToday = () => {
            const today = new Date();
            setMonth(today);
            if (onMonthChange) {
              onMonthChange(today);
            }
          };

          return (
            <div className="flex justify-between items-center w-full px-2">
              <button
                onClick={onPreviousClick}
                aria-label="Previous month"
                className={cn(
                  buttonVariants({ variant: "ghost" }),
                  "h-7 w-7 p-0 text-popover-foreground hover:bg-accent hover:text-accent-foreground"
                )}
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
              <button
                onClick={goToToday}
                aria-label="Go to today"
                className={cn(
                  buttonVariants({ variant: "ghost" }),
                  "h-7 px-3 text-xs font-medium text-popover-foreground hover:bg-accent hover:text-accent-foreground"
                )}
              >
                {translator('Global.today')}
              </button>
              <button
                onClick={onNextClick}
                aria-label="Next month"
                className={cn(
                  buttonVariants({ variant: "ghost" }),
                  "h-7 w-7 p-0 text-popover-foreground hover:bg-accent hover:text-accent-foreground"
                )}
              >
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          );
        },
      }}
      {...props}
    />
  );
}
Calendar.displayName = "Calendar";

export { Calendar };
